import { HugeiconsIcon } from "@hugeicons/react";
import { GridIcon, Settings01Icon, Store01Icon, UserGroupIcon, WrenchIcon } from "@hugeicons-pro/core-bulk-rounded";
import type { IconSvgElement } from "@hugeicons/react";

import { PageHeader } from "@/components/layout/page-header";
import { TeamAccess } from "@/components/settings/team-access";
import { Card } from "@/components/ui/card";
import { FadeIn } from "@/components/ui/motion";
import { PlatformLogo } from "@/components/ui/platform-logo";
import { RegionFlag } from "@/components/ui/region-flag";
import { useReference } from "@/lib/use-reference";

function AccentIcon({ icon: Icon }: { icon: IconSvgElement }) {
  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-primary/15">
      <HugeiconsIcon icon={Icon} size={18} />
    </span>
  );
}

function SectionCard({ icon, title, description, right, flush, children }: { icon: IconSvgElement; title: string; description?: string; right?: React.ReactNode; flush?: boolean; children: React.ReactNode }) {
  return (
    <Card className="overflow-hidden p-0">
      <div className="band-header flex items-center gap-2.5 px-5 py-4">
        <AccentIcon icon={icon} />
        <div className="min-w-0 flex-1">
          <h3 className="font-heading text-base font-semibold">{title}</h3>
          {description && <p className="truncate text-xs text-muted-foreground">{description}</p>}
        </div>
        {right}
      </div>
      <div className={flush ? "" : "p-5"}>{children}</div>
    </Card>
  );
}

function HeaderStat({ label, value }: { label: string; value: string }) {
  return (
    <span className="flex items-baseline gap-1.5">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-heading font-semibold tabular-nums text-foreground">{value}</span>
    </span>
  );
}

export function Settings() {
  const { brands, platforms, dataSources, isLoading } = useReference();
  const connected = dataSources.filter((s) => s.status === "connected").length;
  const count = (n: number) => (isLoading ? "…" : String(n));
  const channels = platforms.filter((p) => p.kind === "channel");
  const tools = platforms.filter((p) => p.kind === "tool");

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Instellingen"
        icon={Settings01Icon}
        title="Instellingen"
        description="Account, organisatie en de bouwstenen van het dashboard."
        tabs={
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
            <HeaderStat label="Merken" value={count(brands.length)} />
            <span className="h-4 w-px bg-border" />
            <HeaderStat label="Platformen" value={count(platforms.length)} />
            <span className="h-4 w-px bg-border" />
            <HeaderStat label="Verbonden bronnen" value={isLoading ? "…" : `${connected} / ${dataSources.length}`} />
          </div>
        }
      />

      <FadeIn>
        <SectionCard
          icon={UserGroupIcon}
          title="Leden"
          description="Personen in deze organisatie."
          flush
        >
          <TeamAccess />
        </SectionCard>
      </FadeIn>

      <FadeIn delay={0.05}>
        <SectionCard icon={Store01Icon} title="Merken" description="De merken waarover het dashboard rapporteert.">
          <div className="flex flex-wrap gap-2">
            {brands.map((b) => (
              <div key={b.id} className="flex items-center gap-2 rounded-lg border border-border/60 py-1.5 pl-2.5 pr-3">
                <RegionFlag region={b.home_region} size={16} />
                <span className="text-sm font-medium">{b.name}</span>
              </div>
            ))}
          </div>
        </SectionCard>
      </FadeIn>

      <FadeIn delay={0.1}>
        <SectionCard icon={GridIcon} title="Platformen" description="Channels leveren media-data; tools voeden analytics.">
          <div className="space-y-4">
            <div>
              <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground/60">Channels</p>
              <div className="flex flex-wrap gap-2">
                {channels.map((p) => (
                  <div key={p.id} className="flex items-center gap-2 rounded-lg border border-border/60 py-1.5 pl-1.5 pr-3">
                    <PlatformLogo platform={p.key} accentColor={p.accent_color} size={18} />
                    <span className="text-sm font-medium">{p.name}</span>
                  </div>
                ))}
              </div>
            </div>
            {tools.length > 0 && (
              <div>
                <p className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground/60">
                  <HugeiconsIcon icon={WrenchIcon} size={12} /> Tools
                </p>
                <div className="flex flex-wrap gap-2">
                  {tools.map((p) => (
                    <div key={p.id} className="flex items-center gap-2 rounded-lg border border-border/60 py-1.5 pl-1.5 pr-3">
                      <PlatformLogo platform={p.key} accentColor={p.accent_color} size={18} />
                      <span className="text-sm font-medium">{p.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </SectionCard>
      </FadeIn>

    </div>
  );
}
